import React, { ComponentProps, ReactNode, useCallback } from 'react';
import { STATIC_DEFAULT_SUPPORT } from '../constants';
import { Text } from '../globalStyles';
import { SupportLinkMethod, useDefaultSupportLinkQuery } from '../graphQL';
import { useCurrentProviderOrNull } from './Permissions';

export type SupportLinkValue = {
  method: SupportLinkMethod;
  link: string;
};

// Resolves the support link: the session's role-resolved value, then the
// anonymous default from the API, then the static constant (which doubles as
// the instant placeholder while the anonymous query resolves).
export const useSupportLink = (): SupportLinkValue => {
  const session = useCurrentProviderOrNull();
  const { data } = useDefaultSupportLinkQuery({ skip: session !== null });
  return session?.supportLink ?? data?.defaultSupportLink ?? STATIC_DEFAULT_SUPPORT;
};

// Spread onto a bare <a> when a call site keeps its own markup: http links must open in a
// new tab (never navigate away from an in-progress flow); mailto stays same-tab.
export const supportLinkAnchorProps = ({ method, link }: SupportLinkValue) =>
  method === SupportLinkMethod.Web
    ? ({ href: link, target: '_blank', rel: 'noopener noreferrer' } as const)
    : ({ href: link } as const);

type SupportLinkProps = {
  children?: ReactNode;
  className?: string;
  kind?: ComponentProps<typeof Text.externalLink>['kind'];
};

export const SupportLink = ({ children, className, kind }: SupportLinkProps): JSX.Element => {
  const supportLink = useSupportLink();
  return (
    <Text.externalLink {...supportLinkAnchorProps(supportLink)} className={className} kind={kind}>
      {children ?? 'contact support'}
    </Text.externalLink>
  );
};

export const useOpenSupportLink = (): (() => void) => {
  const { method, link } = useSupportLink();
  return useCallback(() => {
    if (method === SupportLinkMethod.Email) {
      window.location.href = link;
    } else {
      window.open(link, '_blank', 'noopener');
    }
  }, [method, link]);
};
